import { useLocalSearchParams, useRouter } from 'expo-router'
import { useMemo, useState } from 'react'
import { FlatList, StyleSheet, View } from 'react-native'
import { Icon, Switch, Text, useTheme } from 'react-native-paper'
import { useMembers } from '@/hooks/people'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useHeaderOptions } from '@/hooks/useHeaderOptions'
import { formatJoinDate, formatLastActivity } from '@/hooks/utils'
import type { Member } from '@/types/people'
import { Button } from '@/ui/Button'
import { useCollectionRefresh } from '@/ui/CollectionRefreshControl'
import { ConditionalList } from '@/ui/ConditionalList'
import { Link } from '@/ui/Link'
import { ListWrapper } from '@/ui/ListWrapper'
import { PendingInvitesModal } from '@/ui/people/PendingInvitesModal'
import { UserCard } from '@/ui/people/UserCard'

function InactiveToggle({
    isVisible,
    count,
    value,
    onValueChange,
}: {
    isVisible: boolean
    count: number
    value: boolean
    onValueChange: (value: boolean) => void
}) {
    if (!isVisible) return null

    return (
        <View style={styles.toggleRow}>
            <Text variant="bodyMedium">Show disabled ({count})</Text>
            <Switch value={value} onValueChange={onValueChange} />
        </View>
    )
}

export default function TeamPage() {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const router = useRouter()
    const theme = useTheme()
    const { members, isLoading } = useMembers()
    const { role } = useCurrentOrg({ throwIfNotFound: false })
    const [showInvites, setShowInvites] = useState(false)
    const { refreshControl } = useCollectionRefresh(['users', 'user_org'])
    const [showInactive, setShowInactive] = useState(false)

    useHeaderOptions({
        buttons: {
            type: 'create',
            href: `/${params.orgSlug}/settings/team/invite`,
        },
        enabled: role === 'admin',
    })

    const isAdmin = role === 'admin'

    const inactiveCount = useMemo(
        () => members.members.filter(m => m.inactiveAt).length,
        [members.members]
    )

    const filteredMembers = useMemo(
        () => (showInactive ? members.members : members.members.filter(m => !m.inactiveAt)),
        [members.members, showInactive]
    )

    const handleInviteClick = () => {
        router.push(`/${params.orgSlug}/settings/team/invite`)
    }

    const renderMember = ({ item: member }: { item: Member }) => (
        <UserCard
            user={{
                id: member.user.id,
                name: member.user.name,
                email: member.user.email,
                avatar: member.user.avatar,
                role: member.role,
                color: member.color,
                inactive: !!member.inactiveAt,
            }}
            meta={{
                joinedDate: formatJoinDate(member.createdAt),
                lastActive: formatLastActivity(member.user.last_activity),
            }}
            action={<Icon source="chevron-right" size={20} color={theme.colors.onSurfaceVariant} />}
            wrapper={children => (
                <Link href={`/${params.orgSlug}/settings/team/${member.id}`}>{children}</Link>
            )}
        />
    )

    return (
        <ListWrapper>
            {isAdmin && (
                <View style={styles.headerActions}>
                    <Button
                        title="Pending Invites"
                        mode="outlined"
                        icon="email-outline"
                        onPress={() => setShowInvites(true)}
                    />
                    <InactiveToggle
                        isVisible={inactiveCount > 0}
                        count={inactiveCount}
                        value={showInactive}
                        onValueChange={setShowInactive}
                    />
                </View>
            )}
            <ConditionalList
                data={filteredMembers ?? []}
                isLoading={isLoading}
                icon="account-group"
                title="No team members yet"
                subtitle="Invite team members to collaborate on your projects"
                buttonLabel={isAdmin ? 'Invite Member' : undefined}
                onButtonPress={isAdmin ? handleInviteClick : undefined}
            >
                <FlatList
                    data={filteredMembers}
                    keyExtractor={member => member.id}
                    renderItem={renderMember}
                    contentContainerStyle={styles.container}
                    showsVerticalScrollIndicator={false}
                    refreshControl={refreshControl}
                />
            </ConditionalList>
            <PendingInvitesModal visible={showInvites} onDismiss={() => setShowInvites(false)} />
        </ListWrapper>
    )
}

const styles = StyleSheet.create({
    headerActions: {
        paddingHorizontal: 16,
        paddingTop: 12,
        alignItems: 'flex-start',
        gap: 12,
    },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    container: {
        padding: 16,
        gap: 12,
    },
})
